import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PartnershipPreferences = ({ businessProfile, onSave, saving }) => {
  const [preferences, setPreferences] = useState({
    collaboration_interests: [
      'Technology Integration',
      'Joint Ventures',
      'Pilot Projects',
      'Research & Development'
    ],
    project_types: [
      'Smart Infrastructure',
      'Digital Transformation',
      'Sustainability Projects',
      'IoT Implementation'
    ],
    geographic_focus: [
      'North America',
      'Europe',
      'Global'
    ],
    partnership_criteria: {
      company_size: ['Startup', 'SME', 'Enterprise'],
      funding_stage: ['Seed', 'Series A', 'Growth', 'Established'],
      industry_focus: ['Technology', 'Government', 'Healthcare', 'Education']
    },
    preferred_engagement: [
      'Strategic Partnerships',
      'Technical Collaboration',
      'Co-development',
      'Consulting Services'
    ]
  });

  const [customFields, setCustomFields] = useState({
    partnership_goals: '',
    collaboration_timeline: '',
    resource_sharing: '',
    success_metrics: '',
    communication_preferences: ''
  });

  const collaborationTypes = [
    { id: 'tech_integration', label: 'Technology Integration', description: 'Integrate technologies for comprehensive solutions' },
    { id: 'joint_ventures', label: 'Joint Ventures', description: 'Create new business entities together' },
    { id: 'pilot_projects', label: 'Pilot Projects', description: 'Collaborate on proof-of-concept initiatives' },
    { id: 'research_dev', label: 'Research & Development', description: 'Joint R&D for innovative solutions' },
    { id: 'strategic_alliance', label: 'Strategic Alliance', description: 'Long-term strategic partnerships' },
    { id: 'co_marketing', label: 'Co-marketing', description: 'Joint marketing and sales initiatives' }
  ];

  const projectTypes = [
    { id: 'smart_infrastructure', label: 'Smart Infrastructure', icon: 'Building' },
    { id: 'digital_transformation', label: 'Digital Transformation', icon: 'Zap' },
    { id: 'sustainability', label: 'Sustainability Projects', icon: 'Leaf' },
    { id: 'iot_implementation', label: 'IoT Implementation', icon: 'Wifi' },
    { id: 'data_analytics', label: 'Data Analytics', icon: 'BarChart3' },
    { id: 'ai_solutions', label: 'AI Solutions', icon: 'Brain' }
  ];

  const geographicRegions = [
    { id: 'north_america', label: 'North America', countries: ['USA', 'Canada', 'Mexico'] },
    { id: 'europe', label: 'Europe', countries: ['UK', 'Germany', 'France', 'Nordic Countries'] },
    { id: 'asia_pacific', label: 'Asia Pacific', countries: ['Japan', 'Australia', 'Singapore', 'South Korea'] },
    { id: 'latin_america', label: 'Latin America', countries: ['Brazil', 'Argentina', 'Chile', 'Colombia'] },
    { id: 'middle_east_africa', label: 'Middle East & Africa', countries: ['UAE', 'Saudi Arabia', 'South Africa'] },
    { id: 'global', label: 'Global', countries: ['Worldwide coverage'] }
  ];

  const companySizes = [
    { id: 'startup', label: 'Startup', description: '1-50 employees' },
    { id: 'sme', label: 'Small & Medium Enterprise', description: '51-500 employees' },
    { id: 'enterprise', label: 'Enterprise', description: '500+ employees' },
    { id: 'government', label: 'Government Entity', description: 'Public sector organizations' }
  ];

  const handlePreferenceToggle = (category, item) => {
    setPreferences(prev => ({
      ...prev,
      [category]: prev?.[category]?.includes(item)
        ? prev?.[category]?.filter(i => i !== item)
        : [...(prev?.[category] || []), item]
    }));
  };

  const handleCustomFieldChange = (field, value) => {
    setCustomFields(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    const partnershipData = {
      partnership_preferences: {
        ...preferences,
        custom_fields: customFields
      }
    };

    // In a real implementation, this would save to a business_partnerships table
    // For now, we'll simulate saving preferences console.log('Saving partnership preferences:', partnershipData);
    
    // You could call onSave here if you have a backend endpoint
    // const result = await onSave?.('partnerships', partnershipData);
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Partnership Preferences</h3>
        <p className="text-sm text-gray-600 mb-6">
          Define your partnership criteria and collaboration preferences to connect with the right partners.
        </p>
      </div>

      {/* Collaboration Interests */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">Collaboration Interests</h4>
        <p className="text-sm text-gray-600 mb-4">Select the types of collaborations you're interested in:</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {collaborationTypes?.map((type) => (
            <div
              key={type?.id}
              className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                preferences?.collaboration_interests?.includes(type?.label)
                  ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => handlePreferenceToggle('collaboration_interests', type?.label)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="font-medium text-gray-900">{type?.label}</h5>
                  <p className="text-sm text-gray-600 mt-1">{type?.description}</p>
                </div>
                {preferences?.collaboration_interests?.includes(type?.label) && (
                  <Icon name="Check" className="w-5 h-5 text-blue-600" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Project Types */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">Preferred Project Types</h4>
        <p className="text-sm text-gray-600 mb-4">What types of projects are you most interested in?</p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {projectTypes?.map((project) => (
            <div
              key={project?.id}
              className={`border rounded-lg p-4 cursor-pointer transition-colors text-center ${
                preferences?.project_types?.includes(project?.label)
                  ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => handlePreferenceToggle('project_types', project?.label)}
            >
              <Icon name={project?.icon} className="w-8 h-8 mx-auto mb-2 text-gray-600" />
              <h5 className="font-medium text-gray-900">{project?.label}</h5>
              {preferences?.project_types?.includes(project?.label) && (
                <Icon name="Check" className="w-5 h-5 text-blue-600 mx-auto mt-2" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Geographic Focus */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">Geographic Focus</h4>
        <p className="text-sm text-gray-600 mb-4">Which regions are you targeting for partnerships?</p>
        
        <div className="space-y-3">
          {geographicRegions?.map((region) => (
            <div
              key={region?.id}
              className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                preferences?.geographic_focus?.includes(region?.label)
                  ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => handlePreferenceToggle('geographic_focus', region?.label)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="font-medium text-gray-900">{region?.label}</h5>
                  <p className="text-sm text-gray-600">{region?.countries?.join(', ')}</p>
                </div>
                {preferences?.geographic_focus?.includes(region?.label) && (
                  <Icon name="Check" className="w-5 h-5 text-blue-600" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Partner Criteria */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">Partner Criteria</h4>
        <p className="text-sm text-gray-600 mb-4">Define the characteristics of your ideal partners:</p>
        
        <div className="space-y-6">
          <div>
            <h5 className="font-medium text-gray-900 mb-3">Company Size Preference</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {companySizes?.map((size) => (
                <div
                  key={size?.id}
                  className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                    preferences?.partnership_criteria?.company_size?.includes(size?.label)
                      ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => {
                    setPreferences(prev => ({
                      ...prev,
                      partnership_criteria: {
                        ...prev?.partnership_criteria,
                        company_size: prev?.partnership_criteria?.company_size?.includes(size?.label)
                          ? prev?.partnership_criteria?.company_size?.filter(s => s !== size?.label)
                          : [...(prev?.partnership_criteria?.company_size || []), size?.label]
                      }
                    }));
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h6 className="font-medium text-gray-900">{size?.label}</h6>
                      <p className="text-sm text-gray-600">{size?.description}</p>
                    </div>
                    {preferences?.partnership_criteria?.company_size?.includes(size?.label) && (
                      <Icon name="Check" className="w-4 h-4 text-blue-600" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Partnership Goals & Timeline */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">Partnership Goals & Details</h4>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Partnership Goals
            </label>
            <textarea
              value={customFields?.partnership_goals}
              onChange={(e) => handleCustomFieldChange('partnership_goals', e?.target?.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Describe your strategic objectives for partnerships..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferred Timeline
            </label>
            <textarea
              value={customFields?.collaboration_timeline}
              onChange={(e) => handleCustomFieldChange('collaboration_timeline', e?.target?.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="How quickly do you want to establish partnerships? Any specific timeline requirements?"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Resource Sharing
            </label>
            <textarea
              value={customFields?.resource_sharing}
              onChange={(e) => handleCustomFieldChange('resource_sharing', e?.target?.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="What resources can you contribute? What do you expect from partners?"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Success Metrics
            </label>
            <textarea
              value={customFields?.success_metrics}
              onChange={(e) => handleCustomFieldChange('success_metrics', e?.target?.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="How do you measure partnership success?"
            />
          </div>
        </div>
      </div>

      {/* Communication Preferences */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">Communication Preferences</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferred Communication Method
            </label>
            <select
              value={customFields?.communication_preferences}
              onChange={(e) => handleCustomFieldChange('communication_preferences', e?.target?.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select preference</option>
              <option value="email">Email</option>
              <option value="phone">Phone</option>
              <option value="video_call">Video Call</option>
              <option value="in_person">In-Person Meeting</option>
              <option value="messaging_platform">Messaging Platform</option>
            </select>
          </div>

          <div className="space-y-4">
            <h5 className="font-medium text-gray-900">Availability</h5>
            <div className="space-y-2">
              <label className="flex items-center">
                <input type="checkbox" className="rounded border-gray-300 mr-2" />
                <span className="text-sm text-gray-700">Available for introductory calls</span>
              </label>
              <label className="flex items-center">
                <input type="checkbox" className="rounded border-gray-300 mr-2" />
                <span className="text-sm text-gray-700">Open to networking events</span>
              </label>
              <label className="flex items-center">
                <input type="checkbox" className="rounded border-gray-300 mr-2" />
                <span className="text-sm text-gray-700">Interested in joint presentations</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Matching Algorithm Optimization */}
      <div className="bg-blue-50 rounded-lg p-6">
        <div className="flex items-start">
          <Icon name="Lightbulb" className="w-6 h-6 text-blue-600 mt-1 mr-3" />
          <div>
            <h4 className="text-md font-medium text-blue-900 mb-2">Smart Matching</h4>
            <p className="text-sm text-blue-800 mb-3">
              Your preferences help our algorithm find the most compatible partners. The more detailed your preferences, 
              the better matches you'll receive.
            </p>
            <div className="text-sm text-blue-700">
              <p><strong>Completion Level:</strong> 75% - Add more details to improve matches</p>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-6 border-t">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Partnership Preferences'}
        </Button>
      </div>
    </div>
  );
};

export default PartnershipPreferences;